import * as React from 'react';
import { cn } from '@/utils/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { MeterCanvas } from '@/components/ui/meter-canvas';
import { meterPercent } from '@/components/ui/meter-scale';

/** A limit on the meter: where it sits (0 to 1), whether it is fine, heavy or exceeded, and what it is. */
export interface CapacityMark {
  at: number;
  state?: 'ok' | 'warn' | 'blocked';
  /** What the limit is, shown on hover. */
  label: React.ReactNode;
  /** In the scale layout: above the mark (its amount) and below it (who it limits). */
  above?: React.ReactNode;
  below?: React.ReactNode;
}

interface CapacityMeterProps {
  /** How much of the meter is used, 0 to 1. */
  value: number;
  /** What it measures, for screen readers. */
  label: string;
  marks: CapacityMark[];
  /** bar: the readout in a bar (the editor's top bar), with its leading button and amount; scale: the meter in a panel,
   * each mark's amount above it and who it limits below. */
  layout?: 'bar' | 'scale';
  /** The button before the meter that opens its detail. */
  leading?: React.ReactNode;
  /** The amount used, and its unit (12.40 MB). */
  amount?: React.ReactNode;
  unit?: string;
  /** In a bar the amount shows on hover (a parent with group/pill), for a moment after it changes, and while this is on (its panel open). */
  amountShown?: boolean;
  /** In a bar, words that take the meter's place (its panel's verdict, while the panel is open), the amount staying in its place. */
  status?: React.ReactNode;
  className?: string;
}

// The tube's size, as production painted it
const W = 176;
const H = 36;

// A limit's line sits on a whole pixel, so its dot is centred on that pixel, not on the unrounded spot
const lineCentre = (at: number) => `${Math.round((meterPercent(at, W, H) / 100) * W) + 0.5}px`;

/** A limit's line, on a whole pixel so it draws crisp, with a 16px hit area round it and its name on hover. */
function Limit({ mark, pulse }: { mark: CapacityMark; pulse?: number }) {
  return (
    <Tooltip delayDuration={120}>
      <TooltipTrigger asChild>
        <div className="group absolute inset-y-0 w-4" style={{ left: `${Math.round((meterPercent(mark.at, W, H) / 100) * W) - 8}px` }}>
          <div className="absolute inset-y-0 left-2 w-px bg-lureo-ink transition-all duration-lureo-duration-fast group-hover:w-0.5 group-hover:scale-y-110" />
          {pulse ? <span key={pulse} aria-hidden className="pointer-events-none absolute left-2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-lureo-brand/55 animate-meter-pulse" /> : null}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={10}>
        {mark.label}
      </TooltipContent>
    </Tooltip>
  );
}

/** A heavy or exceeded limit's marked dot: coral with "!", or the brand with an x. Odd-sized so it centres on its 1px line, its x on whole pixels inside. */
function StateDot({ state, className, style }: { state: 'warn' | 'blocked'; className?: string; style?: React.CSSProperties }) {
  return (
    <span aria-hidden className={cn('pointer-events-none absolute z-10 flex h-3.75 w-3.75 -translate-x-1/2 items-center justify-center rounded-full text-lureo-on-emphasis', state === 'warn' ? 'bg-lureo-coral' : 'bg-lureo-brand', className)} style={style}>
      {state === 'warn' ? (
        <span className="text-lureo-10 font-medium leading-none">!</span>
      ) : (
        <svg viewBox="0 0 8 8" className="h-2.25 w-2.25" fill="none" stroke="currentColor" strokeWidth={1.1} strokeLinecap="round">
          <path d="M1.5 1.5l5 5M6.5 1.5l-5 5" />
        </svg>
      )}
    </span>
  );
}

/**
 * How much of a set of limits something uses (a story's size against each platform's), as production drew it: a painted
 * tube turning from fine to heavy along its length, each limit a crisp line with what it is on hover, a marked dot where
 * a limit is heavy or exceeded, and a ring spreading from a limit as it becomes exceeded. In a bar it is glass over the
 * scene with a grey haze beyond the fill; in a panel the whole ramp shows, each limit's amount above it and who it limits below.
 */
export function CapacityMeter({ value, label, marks, layout = 'bar', leading, amount, unit, amountShown = false, status, className }: CapacityMeterProps) {
  const fraction = Math.min(Math.max(value, 0), 1);
  // A ring spreads from a mark each time its limit becomes exceeded
  const states = marks.map(mark => mark.state ?? 'ok').join('|');
  const [seenStates, setSeenStates] = React.useState(states);
  const [pulses, setPulses] = React.useState<Record<number, number>>({});
  if (states !== seenStates) {
    const before = seenStates.split('|');
    const next = { ...pulses };
    marks.forEach((mark, index) => {
      if (mark.state === 'blocked' && before[index] !== 'blocked') next[index] = (next[index] ?? 0) + 1;
    });
    setSeenStates(states);
    setPulses(next);
  }
  // The amount shows for a moment after the value changes
  const [recent, setRecent] = React.useState(false);
  const firstValue = React.useRef(true);
  React.useEffect(() => {
    if (firstValue.current) {
      firstValue.current = false;
      return;
    }
    setRecent(true);
    const timer = window.setTimeout(() => setRecent(false), 2000);
    return () => window.clearTimeout(timer);
  }, [amount]);

  const meterProps = { role: 'meter', 'aria-label': label, 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': Math.round(fraction * 100) } as const;

  if (layout === 'scale') {
    return (
      <div className={cn('relative mx-auto w-44', className)}>
        <div className="relative mb-1 h-3.5">
          {marks.map((mark, index) => (
            <span key={index} className="absolute -translate-x-1/2 font-lureo-font-ui text-lureo-10 leading-none tabular-nums text-lureo-ink-subtle" style={{ left: `${meterPercent(mark.at, W, H)}%` }}>
              {mark.above}
            </span>
          ))}
        </div>
        <div {...meterProps} className="relative h-9 w-44">
          <div className="absolute inset-0">
            <MeterCanvas width={W} height={H} value={fraction} saturated />
          </div>
          {marks.map((mark, index) => (
            <Limit key={index} mark={mark} pulse={pulses[index]} />
          ))}
          {marks.map((mark, index) =>
            mark.state && mark.state !== 'ok' ? <StateDot key={`state-${index}`} state={mark.state} className="top-1/2 -translate-y-1/2" style={{ left: lineCentre(mark.at) }} /> : null,
          )}
        </div>
        <div className="relative mt-2 h-3.5">
          {marks.map((mark, index) => (
            <span key={index} className="absolute flex -translate-x-1/2 items-center gap-0.5 text-lureo-brand-ink [&_svg]:h-3 [&_svg]:w-3" style={{ left: `${meterPercent(mark.at, W, H)}%` }}>
              {mark.below}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // The amount sits inside the meter, centred between its last limit and its end, so the bar is no wider than the meter
  const lastMark = marks.length ? Math.max(...marks.map(mark => meterPercent(mark.at, W, H))) : 70;
  return (
    <div className={cn('flex items-center', className)}>
      {leading}
      <div className="relative mx-1">
        <div className="relative h-9 w-44 shrink-0">
          <div {...meterProps} className="relative h-full w-full">
            {/* The glass, inset under the bar's frame as production's mask had it: the tube's soft rim and the limits' ends stay under the frame */}
            <div data-chrome-window className="absolute inset-0.75 overflow-hidden rounded-lureo-radius-button-pill bg-lureo-highlight/15 backdrop-blur-lureo-blur-lg">
              <div className="absolute -inset-0.75">
                <MeterCanvas width={W} height={H} value={fraction} />
              </div>
              <div className="absolute -inset-x-0.75 inset-y-0">
                {marks.map((mark, index) => (
                  <Limit key={index} mark={mark} pulse={pulses[index]} />
                ))}
              </div>
            </div>
          </div>
          {marks.map((mark, index) =>
            // While the panel's verdict shows, the dots fade with the meter, as production's did
            mark.state && mark.state !== 'ok' ? <StateDot key={`state-${index}`} state={mark.state} className={cn('bottom-0 translate-y-1/2 transition-opacity duration-lureo-duration-moderate', status ? 'opacity-0' : 'opacity-100')} style={{ left: lineCentre(mark.at) }} /> : null,
          )}
          {/* While its panel is open, the panel's verdict takes the meter's place, as production's did */}
          <div aria-hidden={!status} className={cn('pointer-events-none absolute inset-0 z-10 flex items-center rounded-lureo-radius-button-pill surface-chrome pl-2 transition-opacity duration-lureo-duration-moderate', status ? 'opacity-100' : 'opacity-0')}>
            <span className="whitespace-nowrap font-lureo-font-ui text-lureo-13 text-lureo-ink">{status}</span>
          </div>
        </div>
        {amount !== undefined && (
          <span
            className={cn('pointer-events-none absolute inset-y-0 right-0 z-20 flex items-center justify-center pr-2 font-lureo-font-ui text-lureo-13 font-semibold tabular-nums text-lureo-brand-ink transition-opacity duration-lureo-duration-fast group-hover/pill:opacity-100', amountShown || recent || status ? 'opacity-100' : 'opacity-0')}
            style={{ left: `${lastMark}%` }}
          >
            {amount}
            {unit && <span className="ml-0.5 text-lureo-11 font-light text-lureo-brand-ink/80">{unit}</span>}
          </span>
        )}
      </div>
    </div>
  );
}
